import { useController } from 'react-hook-form';
import AutocompleteBase, {
  AutocompleteBaseProps,
} from 'components/forms/fields/autocomplete-field/autocomplete-base/autocomplete-base.component';
import AutocompleteOption from '../autocomplete-field/autocomplete-option/autocomplete-option.component';
import { handleMultiSelectChange, Option } from 'util/multi-select';
import { ChangeEvent, useEffect, useMemo } from 'react';
import {
  daysOfWeekOptions,
  daysOfWeekOptionsLong,
} from './days-multiple-select-field.utils';

type DaysMultipleSelectFieldProps = {
  name: string;
  label: string;
  optionType?: 'short' | 'long';
} & Omit<AutocompleteBaseProps, 'options'>;

const DaysMultipleSelectField = ({
  name,
  label,
  optionType = 'short',
  ...rest
}: DaysMultipleSelectFieldProps) => {
  const {
    field,
    fieldState: { error },
  } = useController({ name });

  const options = useMemo(
    () =>
      optionType === 'short' ? daysOfWeekOptions() : daysOfWeekOptionsLong(),
    [optionType],
  );

  useEffect(() => {
    if (field.value?.length === 7) field.onChange([options[0]]);
  }, [field, options]);

  const handleChange = (
    _event: ChangeEvent<HTMLSelectElement>,
    value: Option[],
  ) => {
    const result: Option[] = handleMultiSelectChange(
      value,
      daysOfWeekOptions(),
    );
    field.onChange(result);
  };

  return (
    <AutocompleteBase
      {...field}
      error={error}
      label={label}
      data-testid="DaysMultipleSelectField.AutocompleteBase"
      options={options}
      value={field.value ?? []}
      onChange={handleChange}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      limitTags={2}
      multiple
      renderOption={(props, option, { selected }, ...OptionProps) => (
        <AutocompleteOption
          key={option.id}
          multiple={true}
          option={option}
          selected={selected}
          OptionProps={{ ...OptionProps }}
          {...props}
        />
      )}
      {...rest}
    />
  );
};

export default DaysMultipleSelectField;
